{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "title": "Layout1538",
  "description": "2D layout of Amsterdam c.1538 in local metric coordinates: x = east, y = north, metres, origin 52.3731 N 4.8926 E (the Dam), equirectangular tangent plane with earth radius 6371008.8 m. Ground level is 0 m, the water surface -0.8 m. Every sourceRefs entry is the id of an entry in meta.sources.",
  "type": "object",
  "additionalProperties": false,
  "required": ["version", "meta", "bounds", "water", "land", "streets", "squares", "bridges", "fortifications", "landmarks", "windmills", "gardens", "polders", "harbour", "farms"],
  "properties": {
    "version": {"description": "Schema version.", "const": 1},
    "meta": {"$ref": "#/$defs/Meta"},
    "bounds": {"$ref": "#/$defs/Bounds"},
    "water": {
      "title": "Water",
      "type": "object",
      "additionalProperties": false,
      "required": ["ij", "channels", "areas", "dams"],
      "properties": {
        "ij": {"$ref": "#/$defs/PolygonWithHoles", "description": "Open IJ water bounded by the 1538 shorelines; the Volewijck headland is cut out of it."},
        "channels": {"type": "array", "items": {"$ref": "#/$defs/Channel"}},
        "areas": {"type": "array", "items": {"$ref": "#/$defs/WaterArea"}},
        "dams": {"type": "array", "items": {"$ref": "#/$defs/Dam"}, "description": "Dry ground in the water, taken out of the IJ, the channels and the areas."}
      }
    },
    "land": {
      "title": "Land",
      "type": "object",
      "additionalProperties": false,
      "required": ["cityEnclosure", "lastage", "northShore"],
      "description": "Land masses; water is subtracted at runtime by the renderer.",
      "properties": {
        "cityEnclosure": {"$ref": "#/$defs/Ring", "description": "Walled city: along the city wall and the IJ shore."},
        "lastage": {"$ref": "#/$defs/Ring", "description": "The Lastage suburb east of the Geldersekade, enclosed by the Oudeschans (1516)."},
        "northShore": {"$ref": "#/$defs/Ring", "description": "Land north of the IJ (Waterland, Volewijck headland) inside the bounds."}
      }
    },
    "streets": {"type": "array", "items": {"$ref": "#/$defs/Street"}},
    "squares": {"type": "array", "items": {"$ref": "#/$defs/Square"}},
    "bridges": {"type": "array", "items": {"$ref": "#/$defs/Bridge"}},
    "fortifications": {
      "title": "Fortifications",
      "type": "object",
      "additionalProperties": false,
      "required": ["walls", "towers", "gates"],
      "properties": {
        "walls": {"type": "array", "items": {"$ref": "#/$defs/Wall"}},
        "towers": {"type": "array", "items": {"$ref": "#/$defs/Tower"}},
        "gates": {"type": "array", "items": {"$ref": "#/$defs/Gate"}}
      }
    },
    "landmarks": {"type": "array", "items": {"$ref": "#/$defs/Landmark"}},
    "windmills": {"type": "array", "items": {"$ref": "#/$defs/Windmill"}},
    "gardens": {"type": "array", "items": {"$ref": "#/$defs/Garden"}},
    "polders": {"type": "array", "items": {"$ref": "#/$defs/Polder"}},
    "harbour": {
      "title": "Harbour",
      "type": "object",
      "additionalProperties": false,
      "required": ["palisades", "anchorages", "moorings", "berths"],
      "properties": {
        "palisades": {"type": "array", "items": {"$ref": "#/$defs/Palisade"}},
        "anchorages": {"type": "array", "items": {"$ref": "#/$defs/Anchorage"}},
        "moorings": {"type": "array", "items": {"$ref": "#/$defs/Mooring"}},
        "berths": {"type": "array", "items": {"$ref": "#/$defs/Berth"}}
      }
    },
    "farms": {"type": "array", "items": {"$ref": "#/$defs/Farm"}}
  },
  "$defs": {
    "Vec2": {
      "title": "Vec2",
      "description": "Point [x east, y north] in metres, 0.1 m precision, but for a gate bridge's ends (Bridge.a, Bridge.b).",
      "type": "array",
      "items": {"type": "number"},
      "minItems": 2,
      "maxItems": 2
    },
    "Ring": {
      "title": "Ring",
      "description": "Closed polygon ring without repeated closing vertex (first != last), no self-intersections; outer rings counter-clockwise, holes clockwise. Coordinates in metres.",
      "type": "array",
      "items": {"$ref": "#/$defs/Vec2"},
      "minItems": 3
    },
    "Polyline": {
      "title": "Polyline",
      "description": "Open polyline of at least two points, coordinates in metres.",
      "type": "array",
      "items": {"$ref": "#/$defs/Vec2"},
      "minItems": 2
    },
    "PolygonWithHoles": {
      "title": "PolygonWithHoles",
      "type": "object",
      "additionalProperties": false,
      "required": ["outer", "holes"],
      "properties": {
        "outer": {"$ref": "#/$defs/Ring"},
        "holes": {"type": "array", "items": {"$ref": "#/$defs/Ring"}}
      }
    },
    "SourceRefs": {
      "title": "SourceRefs",
      "description": "Ids of meta.sources entries that are the evidence for this feature.",
      "type": "array",
      "items": {"type": "string"},
      "minItems": 1
    },
    "Id": {"title": "Id", "description": "Identifier, unique across the whole layout.", "type": "string", "minLength": 1},
    "Heading": {"title": "Heading", "description": "Angle in radians, 0 = +x (east), counter-clockwise positive.", "type": "number"},
    "Meta": {
      "title": "Meta",
      "type": "object",
      "additionalProperties": false,
      "required": ["origin", "earthRadius", "units", "axes", "generatedAt", "sources"],
      "properties": {
        "origin": {
          "title": "Origin",
          "type": "object",
          "additionalProperties": false,
          "required": ["lat", "lon"],
          "properties": {
            "lat": {"description": "Origin latitude in degrees (WGS84).", "const": 52.3731},
            "lon": {"description": "Origin longitude in degrees (WGS84).", "const": 4.8926}
          }
        },
        "earthRadius": {"description": "Earth radius used by the projection, metres.", "const": 6371008.8},
        "units": {"const": "m"},
        "axes": {"const": "x=east,y=north"},
        "generatedAt": {"description": "UTC timestamp (ISO 8601) of the build.", "type": "string"},
        "sources": {"type": "array", "items": {"$ref": "#/$defs/Source"}, "minItems": 1}
      }
    },
    "Source": {
      "title": "Source",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "title", "url", "license"],
      "properties": {
        "id": {"type": "string", "minLength": 1},
        "title": {"type": "string"},
        "url": {"type": "string"},
        "license": {"type": "string"}
      }
    },
    "Bounds": {
      "title": "Bounds",
      "description": "Extent of the whole world including the countryside, metres.",
      "type": "object",
      "additionalProperties": false,
      "required": ["minX", "minY", "maxX", "maxY"],
      "properties": {
        "minX": {"description": "Western limit, metres.", "type": "number"},
        "minY": {"description": "Southern limit, metres.", "type": "number"},
        "maxX": {"description": "Eastern limit, metres.", "type": "number"},
        "maxY": {"description": "Northern limit, metres.", "type": "number"}
      }
    },
    "Channel": {
      "title": "Channel",
      "description": "Linear water body swept along its centerline.",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "name", "kind", "centerline", "widths", "quay", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "name": {"type": "string"},
        "kind": {"enum": ["river", "harbour", "canal", "moat", "ditch"]},
        "centerline": {"$ref": "#/$defs/Polyline"},
        "widths": {
          "description": "Full water width in metres at each centerline vertex (one entry per vertex).",
          "type": "array",
          "items": {"description": "Water width, metres.", "type": "number", "exclusiveMinimum": 0},
          "minItems": 2
        },
        "quay": {"description": "Bank finish: brick quay wall, timber sheet piling, or earth bank.", "enum": ["brick", "wood", "earth"]},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "WaterArea": {
      "title": "WaterArea",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "name", "polygon", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "name": {"type": "string"},
        "polygon": {"$ref": "#/$defs/PolygonWithHoles"},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Dam": {
      "title": "Dam",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "polygon", "sourceRefs"],
      "description": "Dry ground in the water: a gate standing in its moat with the forecourt before it and the ground its walls enclose on the city side, where the moat's strip would otherwise run over them.",
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "polygon": {"$ref": "#/$defs/PolygonWithHoles"},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Street": {
      "title": "Street",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "name", "kind", "centerline", "width", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "name": {"type": "string"},
        "kind": {"enum": ["street", "alley", "quay", "dike", "road", "path"]},
        "centerline": {"$ref": "#/$defs/Polyline"},
        "width": {"description": "Street width between house fronts (or crest width of a dike/road), metres.", "type": "number", "exclusiveMinimum": 0},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Square": {
      "title": "Square",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "name", "polygon", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "name": {"type": "string"},
        "polygon": {"$ref": "#/$defs/Ring"},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Bridge": {
      "title": "Bridge",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "a", "b", "width", "kind", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "name": {"type": "string"},
        "a": {"$ref": "#/$defs/Vec2", "description": "Abutment on one bank, metres; to the millimetre on a bridge its gate walls in (gate), else to 0.1 m."},
        "b": {"$ref": "#/$defs/Vec2", "description": "Abutment on the other bank, metres; to the millimetre on a bridge its gate walls in (gate), else to 0.1 m."},
        "width": {"description": "Deck width, metres.", "type": "number", "exclusiveMinimum": 0},
        "kind": {"enum": ["stone-arch", "wood", "draw"]},
        "gate": {"$ref": "#/$defs/Id", "description": "The gate that lifts this drawbridge and walls it in on both sides, so that it has neither lift frame nor railings of its own. Absent on every other bridge."},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Wall": {
      "title": "Wall",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "path", "height", "thickness", "covered", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "path": {"$ref": "#/$defs/Polyline"},
        "height": {"description": "Wall height, metres above ground.", "type": "number", "exclusiveMinimum": 0},
        "thickness": {"description": "Wall thickness, metres.", "type": "number", "exclusiveMinimum": 0},
        "covered": {"description": "True when the wall walk has a roof.", "type": "boolean"},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Tower": {
      "title": "Tower",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "shape", "pos", "radius", "height", "roof", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "name": {"type": "string"},
        "shape": {"enum": ["round", "half-round", "square", "octagonal"]},
        "pos": {"$ref": "#/$defs/Vec2", "description": "Centre of the tower plan, metres."},
        "radius": {"description": "Plan radius (half the width for square towers), metres.", "type": "number", "exclusiveMinimum": 0},
        "height": {"description": "Height of the masonry to the eaves, metres above ground.", "type": "number", "exclusiveMinimum": 0},
        "roof": {"enum": ["cone", "flat", "spire", "hip"]},
        "heading": {"$ref": "#/$defs/Heading", "description": "Direction the round side of a half-round tower faces, where its surviving footprint documents it, radians, 0 = east, counter-clockwise positive. Without it the tower turns its round side to the moat beside its wall."},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Gate": {
      "title": "Gate",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "name", "pos", "heading", "kind", "width", "depth", "height", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "name": {"type": "string"},
        "pos": {"$ref": "#/$defs/Vec2", "description": "Centre of the gate building, metres; for a gate built on its documented plan, the origin of that plan."},
        "heading": {"$ref": "#/$defs/Heading", "description": "Direction of passage (leaving the city), radians, 0 = east, counter-clockwise positive."},
        "kind": {"enum": ["land", "water"]},
        "width": {"description": "Gate building width across the passage, metres.", "type": "number", "exclusiveMinimum": 0},
        "depth": {"description": "Gate building depth along the passage, metres.", "type": "number", "exclusiveMinimum": 0},
        "height": {"description": "Height of the gate building to the eaves, metres above ground.", "type": "number", "exclusiveMinimum": 0},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Landmark": {
      "title": "Landmark",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "name", "kind", "pos", "heading", "footprint", "params", "description", "confidence", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "name": {"type": "string"},
        "kind": {"enum": ["church", "chapel", "townhall", "monastery", "weighhouse", "crane", "gallows", "blockhouse", "ropewalk", "tenterField", "waterGate", "dam", "marketStalls", "other"]},
        "pos": {"$ref": "#/$defs/Vec2", "description": "Reference point of the building (footprint centroid), metres."},
        "heading": {"$ref": "#/$defs/Heading", "description": "Main axis of the building (e.g. nave towards the choir), radians, 0 = east, counter-clockwise positive."},
        "footprint": {"$ref": "#/$defs/Ring"},
        "params": {
          "title": "LandmarkParams",
          "description": "Open record of building parameters for the renderer's builders; all lengths in metres, heights in metres above ground. Keys used: naveLength, naveWidth, naveHeight (to the eaves), aisles (number of side aisles), aisleHeight, choirLength, apse ('polygonal' | 'flat'), transept (boolean), towerHeight (masonry, to the base of the roof/spire), towerWidth, spire ('needle' | 'pyramid' | 'none'), spireHeight, ridgeTurret (boolean), height (eaves height of simple buildings), roof ('hip' | 'flat' | 'cone' | 'gable'), shape ('round' | 'square' | 'rect'), ruin (boolean, a monastery burned down to a roofless shell), roofMaterial ('tile' | 'slate' | 'thatch' | 'lead'), turret (boolean, central roof turret), stories (number of storeys), rowSpacing (distance between the parallel frame rows of a tenter field).",
          "type": "object",
          "additionalProperties": {"anyOf": [{"type": "number"}, {"type": "string"}, {"type": "boolean"}]}
        },
        "description": {"type": "string"},
        "confidence": {"enum": ["high", "medium", "low"]},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Windmill": {
      "title": "Windmill",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "pos", "heading", "kind", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "pos": {"$ref": "#/$defs/Vec2", "description": "Mill position, metres."},
        "heading": {"$ref": "#/$defs/Heading", "description": "Direction the sails face, radians, 0 = east, counter-clockwise positive."},
        "kind": {"enum": ["post", "tower"]},
        "tower": {"$ref": "#/$defs/Id", "description": "The flat-topped wall tower or bastion the mill stands on; its base is that tower's top, not the ground."},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Garden": {
      "title": "Garden",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "polygon", "kind", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "polygon": {"$ref": "#/$defs/Ring"},
        "kind": {"enum": ["garden", "orchard", "cloister", "yard", "field"]},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Polder": {
      "title": "Polder",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "polygon", "ditchAngle", "ditchSpacing", "sourceRefs"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "polygon": {"$ref": "#/$defs/Ring"},
        "ditchAngle": {"description": "Direction of the parallel ditches, radians, 0 = east, counter-clockwise positive (a line direction, so a and a + pi are equivalent).", "type": "number"},
        "ditchSpacing": {"description": "Distance between neighbouring parallel ditches, metres.", "type": "number", "exclusiveMinimum": 0},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Palisade": {
      "title": "Palisade",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "path", "pileSpacing"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "path": {"$ref": "#/$defs/Polyline"},
        "pileSpacing": {"description": "Distance between neighbouring piles, metres.", "type": "number", "exclusiveMinimum": 0},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Anchorage": {
      "title": "Anchorage",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "polygon", "density"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "polygon": {"$ref": "#/$defs/Ring"},
        "density": {"description": "Ships at anchor, ships per hectare.", "type": "number", "minimum": 0},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Craft": {
      "title": "Craft",
      "description": "A vessel by its masts: ship = two or more, smallCraft = one, unmasted = none.",
      "enum": ["ship", "smallCraft", "unmasted"]
    },
    "CraftCounts": {
      "title": "CraftCounts",
      "description": "Numbers of vessels by craft.",
      "type": "object",
      "additionalProperties": false,
      "required": ["ship", "smallCraft", "unmasted"],
      "properties": {
        "ship": {"type": "integer", "minimum": 0},
        "smallCraft": {"type": "integer", "minimum": 0},
        "unmasted": {"type": "integer", "minimum": 0}
      }
    },
    "Mooring": {
      "title": "Mooring",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "path", "side", "craft", "underSail"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "path": {"$ref": "#/$defs/Polyline", "description": "Bank along which boats are moored, metres."},
        "side": {"description": "Side of the path (looking along it) on which the water and the moored boats lie.", "enum": ["left", "right"]},
        "craft": {"$ref": "#/$defs/CraftCounts", "description": "Boats moored along the path."},
        "underSail": {"$ref": "#/$defs/CraftCounts", "description": "Of the boats moored along the path, those with a sail spread."},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Berth": {
      "title": "Berth",
      "description": "A vessel the painting shows in the harbour, where the painter view draws it.",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "pos", "zone", "craft", "sailSet", "salute"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "pos": {"$ref": "#/$defs/Vec2", "description": "Where the vessel lies, metres."},
        "zone": {"description": "Water the vessel lies on: inside the outer pile row, or the roadstead outside it.", "enum": ["innerHarbour", "roadstead"]},
        "craft": {"$ref": "#/$defs/Craft"},
        "sailSet": {"description": "At least one of the vessel's sails is spread.", "type": "boolean"},
        "salute": {"description": "The ends of the vessel that fire a salute, drawn as a puff of powder smoke.", "type": "array", "uniqueItems": true, "items": {"enum": ["bow", "stern"]}},
        "bow": {"description": "The side of the painter frame the vessel's bow points to: as read on the scan by its rig and castles, else where its salute shows it, a puff lying beyond the end that fires it.", "enum": ["left", "right"]},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    },
    "Farm": {
      "title": "Farm",
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "pos", "heading", "size"],
      "properties": {
        "id": {"$ref": "#/$defs/Id"},
        "pos": {"$ref": "#/$defs/Vec2", "description": "Farmhouse position, metres."},
        "heading": {"$ref": "#/$defs/Heading", "description": "Ridge direction of the farmhouse, radians, 0 = east, counter-clockwise positive."},
        "size": {"enum": ["small", "large"]},
        "form": {"enum": ["amstel-dike-house"], "description": "Source-specific elongated gabled house on the Amstel dike; absent for ordinary farm templates."},
        "sourceRefs": {"$ref": "#/$defs/SourceRefs"}
      }
    }
  }
}
